import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

export function formatPhone(value: string | null | undefined): string {
  if (!value) return '';
  const digits = value.replace(/\D+/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length === 10) return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
  return value;
}

export function formatCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return '—';
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  });
}

export function parseCurrencyToCents(value: string): number {
  const cleaned = value.replace(/[^0-9.\-]/g, '');
  if (!cleaned) return 0;
  return Math.round(Number(cleaned) * 100);
}

export function formatDateTime(epochSeconds: number | null | undefined): string {
  if (!epochSeconds) return '—';
  return new Date(epochSeconds * 1000).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function formatDate(epochSeconds: number | null | undefined): string {
  if (!epochSeconds) return '—';
  return new Date(epochSeconds * 1000).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Render a stored 'YYYY-MM-DD' without letting the browser shift the day. */
export function formatLocalDate(date: string | null | undefined): string {
  if (!date) return '—';
  const [y, m, d] = date.split('-').map(Number);
  if (!y || !m || !d) return date;
  return new Date(y, m - 1, d).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * Render a start date at the precision it was actually established.
 *
 * A month- or year-only value is anchored on the first of the period so it can
 * live in a DATE column, and that anchor must never be shown as if someone had
 * verified the day. "2019-01-01" with year precision reads "2019 (year only)".
 */
export function formatStartDate(
  date: string | null | undefined,
  precision: string | null | undefined,
): string {
  if (!date) return '—';
  if (precision === 'year') return `${date.slice(0, 4)} (year only)`;
  if (precision === 'month') {
    const [y, m] = date.split('-').map(Number);
    if (!y || !m) return date;
    const label = new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString(undefined, {
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
    });
    return `${label} (month only)`;
  }
  return formatLocalDate(date);
}

export function formatRelative(epochSeconds: number | null | undefined): string {
  if (!epochSeconds) return 'never';
  const diff = Math.floor(Date.now() / 1000) - epochSeconds;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return formatDate(epochSeconds);
}

export function todayLocalDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function addDaysLocal(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const shifted = new Date(Date.UTC(y, m - 1, d) + days * 86_400_000);
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-${String(
    shifted.getUTCDate(),
  ).padStart(2, '0')}`;
}

/** "8 months" / "2.3 years" from a stored YYYY-MM-DD start date. */
export function formatBusinessAge(startedDate: string | null | undefined): string {
  if (!startedDate) return "—";
  const [y, m, d] = startedDate.split("-").map(Number);
  if (!y || !m || !d) return startedDate;
  const months = (Date.now() - Date.UTC(y, m - 1, d)) / (30.437 * 86400000);
  if (months < 0) return "not yet open";
  if (months < 1) return "under a month";
  if (months < 24) return `${Math.round(months)} months`;
  return `${(months / 12).toFixed(1)} years`;
}

/** Newer businesses read as a stronger opportunity. */
export function businessAgeClass(startedDate: string | null | undefined): string {
  if (!startedDate) return "text-muted-foreground";
  const [y, m, d] = startedDate.split("-").map(Number);
  if (!y) return "text-muted-foreground";
  const months = (Date.now() - Date.UTC(y, m - 1, d)) / (30.437 * 86400000);
  if (months < 12) return "text-emerald-300";
  if (months < 24) return "text-sky-300";
  if (months < 48) return "text-muted-foreground";
  return "text-muted-foreground/60";
}

export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

export function pluralize(count: number, singular: string, plural?: string): string {
  return count === 1 ? singular : (plural ?? `${singular}s`);
}

export function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

/** Tailwind classes for a pipeline status chip. */
export function pipelineStatusClass(status: string): string {
  switch (status) {
    case 'New':
      return 'bg-slate-500/15 text-slate-300 border-slate-500/30';
    case 'Ready to Call':
      return 'bg-sky-500/15 text-sky-300 border-sky-500/30';
    case 'Attempted':
      return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
    case 'Contacted':
      return 'bg-indigo-500/15 text-indigo-300 border-indigo-500/30';
    case 'Interested':
      return 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30';
    case 'Examples Sent':
      return 'bg-teal-500/15 text-teal-300 border-teal-500/30';
    case 'Follow-Up':
      return 'bg-violet-500/15 text-violet-300 border-violet-500/30';
    case 'Proposal Sent':
      return 'bg-fuchsia-500/15 text-fuchsia-300 border-fuchsia-500/30';
    case 'Sold':
      return 'bg-green-500/20 text-green-300 border-green-500/40';
    case 'Not Interested':
      return 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30';
    case 'Bad Lead':
      return 'bg-red-500/15 text-red-300 border-red-500/30';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

export function severityClass(severity: string | null): string {
  switch (severity) {
    case 'Critical':
      return 'bg-red-500/20 text-red-300 border-red-500/40';
    case 'High':
      return 'bg-orange-500/20 text-orange-300 border-orange-500/40';
    case 'Medium':
      return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
    case 'Low':
      return 'bg-sky-500/15 text-sky-300 border-sky-500/30';
    case 'Informational':
      return 'bg-slate-500/15 text-slate-300 border-slate-500/30';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

export function webPresenceClass(status: string): string {
  switch (status) {
    case 'No Website Found':
      return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40';
    case 'Facebook Only':
    case 'Google Profile Only':
    case 'Directory Only':
      return 'bg-amber-500/15 text-amber-300 border-amber-500/30';
    case 'Official Website Found':
      return 'bg-slate-500/15 text-slate-300 border-slate-500/30';
    default:
      return 'bg-muted text-muted-foreground border-border';
  }
}

export function scoreClass(score: number | null): string {
  if (score === null) return 'text-muted-foreground';
  if (score >= 75) return 'text-emerald-300';
  if (score >= 50) return 'text-amber-300';
  if (score >= 25) return 'text-sky-300';
  return 'text-muted-foreground';
}

export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}
